import { GoalIcon, TrendingUpIcon } from 'lucide-react';
import { useState } from 'react';
import { Separator } from '@/components/atoms/separator';
import { FeedbackPrompt } from '@/components/molecules/FeedbackPrompt';
import { ACTION_TRACKED } from '@/libs/mixpanel';
import { STORAGE_KEYS } from '@/libs/storage';
import { formatNumber } from '@/libs/utils/format';
import type { ICapturedAadtExtracted, IMetrics } from '@/types/api';
import {
  CAPTURED_AADT_SEGMENT_IDS,
  type CapturedAadtSegmentId,
  LayerId,
  METRICS_LIST,
} from '../../constants';
import { getSegmentColor, getSegmentId } from '../../constants/capturedAadtSegments';
import type { MapLayersService } from '../../utils/MapLayersService';
import { MetricItem } from '../MetricItem';
import { RouteToggleButton } from './RouteToggleButton';

interface BaseMetricsProps {
  metrics: IMetrics[] | null;
  capturedAadtData?: ICapturedAadtExtracted;
  layersService?: MapLayersService;
  locationDistance: string;
  distanceUnit: string;
  projectId: string;
}

export function BaseMetrics({
  metrics,
  capturedAadtData,
  layersService,
  locationDistance,
  distanceUnit,
  projectId,
}: BaseMetricsProps) {
  const showCapturedAadt = !!capturedAadtData?.routes?.length;

  // CapturedAadt track visibility state for each segment
  const [segmentVisibility, setSegmentVisibility] = useState<
    Record<CapturedAadtSegmentId, boolean>
  >({
    [CAPTURED_AADT_SEGMENT_IDS[0]]: true,
    [CAPTURED_AADT_SEGMENT_IDS[1]]: true,
  });

  function handleSegmentToggle(segmentId: CapturedAadtSegmentId) {
    const newVisibility = !segmentVisibility[segmentId];
    const updatedVisibility = {
      ...segmentVisibility,
      [segmentId]: newVisibility,
    };
    setSegmentVisibility(updatedVisibility);

    // Get list of visible segment IDs
    const visibleSegments = Object.entries(updatedVisibility)
      .filter(([_, visible]) => visible)
      .map(([id]) => id as CapturedAadtSegmentId);

    if (visibleSegments.length === 0) {
      // Hide all captured AADT layers
      layersService?.toggleLayerVisibility(
        [LayerId.CAPTURED_AADT, LayerId.CAPTURED_AADT_BORDER, LayerId.CAPTURED_AADT_MARKER],
        false,
      );
    } else {
      // Show main layers and apply filter
      layersService?.toggleLayerVisibility(
        [LayerId.CAPTURED_AADT, LayerId.CAPTURED_AADT_BORDER, LayerId.CAPTURED_AADT_MARKER],
        true,
      );
      layersService?.filterCapturedAadt(visibleSegments);
    }
  }

  return (
    <div>
      {/* Max AADT is always the primary metric */}
      <MetricItem
        variant="main"
        label="Max AADT"
        metricKey={METRICS_LIST.MAX_AADT}
        metrics={metrics}
        formatValue={formatNumber}
        icon={<TrendingUpIcon className="size-5" />}
      />
      <FeedbackPrompt
        text="Does the AADT look right?"
        eventName={ACTION_TRACKED.FEEDBACK_CAPTURED_AADT}
        projectId={projectId}
        storageKey={STORAGE_KEYS.FEEDBACK_CAPTURED_AADT}
        className="mt-2"
      />

      {/* Captured AADT section when available */}
      {showCapturedAadt && (
        <>
          <Separator className="mt-3 mb-1.5" />
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">Captured AADT</span>
            <span className="text-sm font-bold text-primary-700">
              <GoalIcon className="inline size-3.5 mr-1 align-text-bottom" />
              {formatNumber(capturedAadtData.value)}
            </span>
          </div>
          <div className="flex items-center justify-between mt-2">
            {capturedAadtData.routes.map((route, index) => {
              const segmentId = getSegmentId(index);

              return (
                <RouteToggleButton
                  key={`segment-${segmentId}`}
                  routeIndex={index}
                  grade={route.grade}
                  drivingTime={route.driving_time}
                  color={getSegmentColor(index)}
                  visible={segmentVisibility[segmentId]}
                  onToggle={() => handleSegmentToggle(segmentId)}
                  className={index > 0 ? 'ml-1' : ''}
                />
              );
            })}
          </div>
        </>
      )}

      <Separator className="mt-3 mb-1.5" />

      {/* Radius-based metrics */}
      <div className="font-bold text-sm mb-2">
        Metrics within {locationDistance}
        {distanceUnit} radius
      </div>
      <div className="flex flex-col gap-2">
        <MetricItem
          label="Local population density"
          metricKey={METRICS_LIST.LOCAL_POPULATION_DENSITY}
          metrics={metrics}
          formatValue={formatNumber}
        />
      </div>
    </div>
  );
}
